import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Alert,
  Snackbar,
  Tabs,
  Tab,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Divider,
  InputAdornment,
  IconButton
} from '@mui/material';
import {
  AttachMoney,
  Settings as SettingsIcon,
  Tune,
  VpnKey,
  Save,
  Refresh,
  Visibility,
  VisibilityOff,
  Warning
} from '@mui/icons-material';
import axios from 'axios';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const SettingsPage: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Financial Settings
  const [financialSettings, setFinancialSettings] = useState({
    commissionRate: 15,
    minimumFare: 5,
    maximumFare: 500,
    cancellationFeeDriver: 5,
    cancellationFeePassenger: 3,
    cancellationTimeLimit: 24,
    refundProcessingDays: 7
  });

  // Operational Settings
  const [operationalSettings, setOperationalSettings] = useState({
    rideVisibilityDays: 30,
    autoAcceptBookings: false,
    maxSeatsPerRide: 6,
    allowedCountries: ['CA', 'US'],
    supportedLanguages: ['en', 'fr'],
    minDriverAge: 21,
    minPassengerAge: 18
  });

  // Feature Toggles
  const [featureToggles, setFeatureToggles] = useState({
    newUserRegistration: true,
    driverApplications: true,
    promotionalCampaigns: true,
    maintenanceMode: false,
    emailVerification: true,
    phoneVerification: true,
    twoFactorAuth: false
  });

  // API Keys
  const [apiKeys, setApiKeys] = useState({
    googleMapsApiKey: '',
    stripePublishableKey: '',
    stripeSecretKey: '',
    twilioAccountSid: '',
    twilioVerifyServiceSid: ''
  });

  const [showApiKeys, setShowApiKeys] = useState({
    googleMapsApiKey: false,
    stripePublishableKey: false,
    stripeSecretKey: false,
    twilioAccountSid: false,
    twilioVerifyServiceSid: false
  });

  const [alert, setAlert] = useState<{ show: boolean; message: string; severity: 'success' | 'error' | 'info' | 'warning' }>({
    show: false,
    message: '',
    severity: 'success'
  });

  const apiUrl = process.env.NODE_ENV === 'production'
    ? 'https://rideclubnet.com/api'
    : 'http://localhost:3001/api';

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const headers = { Authorization: `Bearer ${token}` };

      const response = await axios.get(`${apiUrl}/admin/settings`, { headers });
      const settings = response.data;

      if (settings.financial) setFinancialSettings(settings.financial);
      if (settings.operational) setOperationalSettings(settings.operational);
      if (settings.features) setFeatureToggles(settings.features);
      if (settings.apiKeys) {
        // Mask API keys for security
        const maskedKeys = { ...settings.apiKeys };
        Object.keys(maskedKeys).forEach(key => {
          if (maskedKeys[key]) {
            maskedKeys[key] = maskedKeys[key].substring(0, 8) + '••••••••';
          }
        });
        setApiKeys(maskedKeys);
      }
    } catch (error: any) {
      console.error('Failed to fetch settings:', error);
      showAlert('error', error.response?.data?.message || 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveFinancial = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      await axios.put(
        `${apiUrl}/admin/settings/financial`,
        financialSettings,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      showAlert('success', 'Financial settings saved successfully');
      setHasChanges(false);
    } catch (error: any) {
      showAlert('error', error.response?.data?.message || 'Failed to save settings');
    }
  };

  const handleSaveOperational = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      await axios.put(
        `${apiUrl}/admin/settings/operational`,
        operationalSettings,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      showAlert('success', 'Operational settings saved successfully');
      setHasChanges(false);
    } catch (error: any) {
      showAlert('error', error.response?.data?.message || 'Failed to save settings');
    }
  };

  const handleSaveFeatures = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      await axios.put(
        `${apiUrl}/admin/settings/features`,
        featureToggles,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      showAlert('success', 'Feature toggles saved successfully');
      setHasChanges(false);
    } catch (error: any) {
      showAlert('error', error.response?.data?.message || 'Failed to save settings');
    }
  };

  const handleSaveApiKeys = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      await axios.put(
        `${apiUrl}/admin/settings/api-keys`,
        apiKeys,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      showAlert('success', 'API keys saved successfully');
      setHasChanges(false);
    } catch (error: any) {
      showAlert('error', error.response?.data?.message || 'Failed to save API keys');
    }
  };

  const showAlert = (severity: 'success' | 'error' | 'info' | 'warning', message: string) => {
    setAlert({ show: true, message, severity });
  };

  const toggleApiKeyVisibility = (key: string) => {
    setShowApiKeys(prev => ({
      ...prev,
      [key]: !prev[key as keyof typeof prev]
    }));
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" fontWeight="bold">
          System Settings & Configuration
        </Typography>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={fetchSettings}
        >
          Reload Settings
        </Button>
      </Box>

      {hasChanges && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          You have unsaved changes. Make sure to save before leaving this page.
        </Alert>
      )}

      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
          <Tab icon={<AttachMoney />} label="Financial" iconPosition="start" />
          <Tab icon={<Tune />} label="Operational" iconPosition="start" />
          <Tab icon={<SettingsIcon />} label="Features" iconPosition="start" />
          <Tab icon={<VpnKey />} label="API Keys" iconPosition="start" />
        </Tabs>
      </Paper>

      {/* Financial Settings Tab */}
      <TabPanel value={tabValue} index={0}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom fontWeight="bold">
              Financial Configuration
            </Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
              Configure platform commission rates, fees, and refund policies
            </Typography>

            <Divider sx={{ my: 3 }} />

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
              <TextField
                label="Platform Commission Rate"
                type="number"
                value={financialSettings.commissionRate}
                onChange={(e) => {
                  setFinancialSettings({ ...financialSettings, commissionRate: parseFloat(e.target.value) });
                  setHasChanges(true);
                }}
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>
                }}
                helperText="Percentage of each ride fare"
              />

              <TextField
                label="Minimum Ride Fare"
                type="number"
                value={financialSettings.minimumFare}
                onChange={(e) => {
                  setFinancialSettings({ ...financialSettings, minimumFare: parseFloat(e.target.value) });
                  setHasChanges(true);
                }}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>
                }}
              />

              <TextField
                label="Maximum Ride Fare"
                type="number"
                value={financialSettings.maximumFare}
                onChange={(e) => {
                  setFinancialSettings({ ...financialSettings, maximumFare: parseFloat(e.target.value) });
                  setHasChanges(true);
                }}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>
                }}
              />

              <TextField
                label="Driver Cancellation Fee"
                type="number"
                value={financialSettings.cancellationFeeDriver}
                onChange={(e) => {
                  setFinancialSettings({ ...financialSettings, cancellationFeeDriver: parseFloat(e.target.value) });
                  setHasChanges(true);
                }}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>
                }}
                helperText="Fee charged to driver for late cancellation"
              />

              <TextField
                label="Passenger Cancellation Fee"
                type="number"
                value={financialSettings.cancellationFeePassenger}
                onChange={(e) => {
                  setFinancialSettings({ ...financialSettings, cancellationFeePassenger: parseFloat(e.target.value) });
                  setHasChanges(true);
                }}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>
                }}
                helperText="Fee charged to passenger for late cancellation"
              />

              <TextField
                label="Cancellation Time Limit"
                type="number"
                value={financialSettings.cancellationTimeLimit}
                onChange={(e) => {
                  setFinancialSettings({ ...financialSettings, cancellationTimeLimit: parseInt(e.target.value) });
                  setHasChanges(true);
                }}
                InputProps={{
                  endAdornment: <InputAdornment position="end">hours</InputAdornment>
                }}
                helperText="Free cancellation before this time"
              />

              <TextField
                label="Refund Processing Days"
                type="number"
                value={financialSettings.refundProcessingDays}
                onChange={(e) => {
                  setFinancialSettings({ ...financialSettings, refundProcessingDays: parseInt(e.target.value) });
                  setHasChanges(true);
                }}
                InputProps={{
                  endAdornment: <InputAdornment position="end">days</InputAdornment>
                }}
                helperText="Time to process refunds"
              />
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <Button
                variant="contained"
                startIcon={<Save />}
                onClick={handleSaveFinancial}
              >
                Save Financial Settings
              </Button>
            </Box>
          </CardContent>
        </Card>
      </TabPanel>

      {/* Operational Settings Tab */}
      <TabPanel value={tabValue} index={1}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom fontWeight="bold">
              Operational Configuration
            </Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
              Configure ride visibility, booking rules, and regional settings
            </Typography>

            <Divider sx={{ my: 3 }} />

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
              <TextField
                label="Ride Visibility Period"
                type="number"
                value={operationalSettings.rideVisibilityDays}
                onChange={(e) => {
                  setOperationalSettings({ ...operationalSettings, rideVisibilityDays: parseInt(e.target.value) });
                  setHasChanges(true);
                }}
                InputProps={{
                  endAdornment: <InputAdornment position="end">days</InputAdornment>
                }}
                helperText="How far in advance drivers can post rides"
              />

              <TextField
                label="Maximum Seats Per Ride"
                type="number"
                value={operationalSettings.maxSeatsPerRide}
                onChange={(e) => {
                  setOperationalSettings({ ...operationalSettings, maxSeatsPerRide: parseInt(e.target.value) });
                  setHasChanges(true);
                }}
                helperText="Maximum capacity per vehicle"
              />

              <TextField
                label="Minimum Driver Age"
                type="number"
                value={operationalSettings.minDriverAge}
                onChange={(e) => {
                  setOperationalSettings({ ...operationalSettings, minDriverAge: parseInt(e.target.value) });
                  setHasChanges(true);
                }}
                InputProps={{
                  endAdornment: <InputAdornment position="end">years</InputAdornment>
                }}
              />

              <TextField
                label="Minimum Passenger Age"
                type="number"
                value={operationalSettings.minPassengerAge}
                onChange={(e) => {
                  setOperationalSettings({ ...operationalSettings, minPassengerAge: parseInt(e.target.value) });
                  setHasChanges(true);
                }}
                InputProps={{
                  endAdornment: <InputAdornment position="end">years</InputAdornment>
                }}
              />

              <FormControl fullWidth>
                <InputLabel>Allowed Countries</InputLabel>
                <Select
                  multiple
                  value={operationalSettings.allowedCountries}
                  label="Allowed Countries"
                  onChange={(e) => {
                    setOperationalSettings({ ...operationalSettings, allowedCountries: e.target.value as string[] });
                    setHasChanges(true);
                  }}
                >
                  <MenuItem value="CA">Canada</MenuItem>
                  <MenuItem value="US">United States</MenuItem>
                  <MenuItem value="MX">Mexico</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Supported Languages</InputLabel>
                <Select
                  multiple
                  value={operationalSettings.supportedLanguages}
                  label="Supported Languages"
                  onChange={(e) => {
                    setOperationalSettings({ ...operationalSettings, supportedLanguages: e.target.value as string[] });
                    setHasChanges(true);
                  }}
                >
                  <MenuItem value="en">English</MenuItem>
                  <MenuItem value="fr">French</MenuItem>
                  <MenuItem value="es">Spanish</MenuItem>
                  <MenuItem value="zh-CN">Simplified Chinese (简体中文)</MenuItem>
                  <MenuItem value="zh-TW">Traditional Chinese (繁體中文)</MenuItem>
                  <MenuItem value="pa">Punjabi (ਪੰਜਾਬੀ)</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Divider sx={{ my: 3 }} />

            <FormControlLabel
              control={
                <Switch
                  checked={operationalSettings.autoAcceptBookings}
                  onChange={(e) => {
                    setOperationalSettings({ ...operationalSettings, autoAcceptBookings: e.target.checked });
                    setHasChanges(true);
                  }}
                />
              }
              label="Auto-Accept Bookings (when driver capacity allows)"
            />

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <Button
                variant="contained"
                startIcon={<Save />}
                onClick={handleSaveOperational}
              >
                Save Operational Settings
              </Button>
            </Box>
          </CardContent>
        </Card>
      </TabPanel>

      {/* Feature Toggles Tab */}
      <TabPanel value={tabValue} index={2}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom fontWeight="bold">
              Feature Toggles
            </Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
              Enable or disable platform features and functionality
            </Typography>

            <Divider sx={{ my: 3 }} />

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={featureToggles.newUserRegistration}
                      onChange={(e) => {
                        setFeatureToggles({ ...featureToggles, newUserRegistration: e.target.checked });
                        setHasChanges(true);
                      }}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body1" fontWeight="medium">New User Registration</Typography>
                      <Typography variant="caption" color="textSecondary">
                        Allow new users to create accounts
                      </Typography>
                    </Box>
                  }
                />
              </Paper>

              <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={featureToggles.driverApplications}
                      onChange={(e) => {
                        setFeatureToggles({ ...featureToggles, driverApplications: e.target.checked });
                        setHasChanges(true);
                      }}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body1" fontWeight="medium">Driver Applications</Typography>
                      <Typography variant="caption" color="textSecondary">
                        Accept new driver registration and verification
                      </Typography>
                    </Box>
                  }
                />
              </Paper>

              <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={featureToggles.promotionalCampaigns}
                      onChange={(e) => {
                        setFeatureToggles({ ...featureToggles, promotionalCampaigns: e.target.checked });
                        setHasChanges(true);
                      }}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body1" fontWeight="medium">Promotional Campaigns</Typography>
                      <Typography variant="caption" color="textSecondary">
                        Display promotional banners and offers
                      </Typography>
                    </Box>
                  }
                />
              </Paper>

              <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={featureToggles.emailVerification}
                      onChange={(e) => {
                        setFeatureToggles({ ...featureToggles, emailVerification: e.target.checked });
                        setHasChanges(true);
                      }}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body1" fontWeight="medium">Email Verification</Typography>
                      <Typography variant="caption" color="textSecondary">
                        Require email verification for new accounts
                      </Typography>
                    </Box>
                  }
                />
              </Paper>

              <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={featureToggles.phoneVerification}
                      onChange={(e) => {
                        setFeatureToggles({ ...featureToggles, phoneVerification: e.target.checked });
                        setHasChanges(true);
                      }}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body1" fontWeight="medium">Phone Verification</Typography>
                      <Typography variant="caption" color="textSecondary">
                        Require phone number verification via SMS
                      </Typography>
                    </Box>
                  }
                />
              </Paper>

              <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={featureToggles.twoFactorAuth}
                      onChange={(e) => {
                        setFeatureToggles({ ...featureToggles, twoFactorAuth: e.target.checked });
                        setHasChanges(true);
                      }}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body1" fontWeight="medium">Two-Factor Authentication</Typography>
                      <Typography variant="caption" color="textSecondary">
                        Enable 2FA for admin accounts
                      </Typography>
                    </Box>
                  }
                />
              </Paper>

              <Paper sx={{ p: 2, bgcolor: featureToggles.maintenanceMode ? 'error.light' : 'grey.50' }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={featureToggles.maintenanceMode}
                      onChange={(e) => {
                        setFeatureToggles({ ...featureToggles, maintenanceMode: e.target.checked });
                        setHasChanges(true);
                      }}
                      color="error"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body1" fontWeight="medium" color={featureToggles.maintenanceMode ? 'error.main' : 'inherit'}>
                        Maintenance Mode
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Temporarily disable platform for maintenance (users will see maintenance page)
                      </Typography>
                    </Box>
                  }
                />
              </Paper>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <Button
                variant="contained"
                startIcon={<Save />}
                onClick={handleSaveFeatures}
              >
                Save Feature Toggles
              </Button>
            </Box>
          </CardContent>
        </Card>
      </TabPanel>

      {/* API Keys Tab */}
      <TabPanel value={tabValue} index={3}>
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="subtitle2" fontWeight="bold">Security Warning</Typography>
          API keys are sensitive credentials. Only update when necessary and ensure proper key rotation.
        </Alert>

        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom fontWeight="bold">
              API Key Management
            </Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
              Configure third-party service integrations and API credentials
            </Typography>

            <Divider sx={{ my: 3 }} />

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <TextField
                label="Google Maps API Key"
                type={showApiKeys.googleMapsApiKey ? 'text' : 'password'}
                value={apiKeys.googleMapsApiKey}
                onChange={(e) => {
                  setApiKeys({ ...apiKeys, googleMapsApiKey: e.target.value });
                  setHasChanges(true);
                }}
                fullWidth
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => toggleApiKeyVisibility('googleMapsApiKey')}>
                        {showApiKeys.googleMapsApiKey ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
                helperText="For geocoding and map display"
              />

              <TextField
                label="Twilio Account SID"
                type={showApiKeys.twilioAccountSid ? 'text' : 'password'}
                value={apiKeys.twilioAccountSid}
                onChange={(e) => {
                  setApiKeys({ ...apiKeys, twilioAccountSid: e.target.value });
                  setHasChanges(true);
                }}
                fullWidth
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => toggleApiKeyVisibility('twilioAccountSid')}>
                        {showApiKeys.twilioAccountSid ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
                helperText="For SMS and phone verification"
              />

              <TextField
                label="Twilio Verify Service SID"
                type={showApiKeys.twilioVerifyServiceSid ? 'text' : 'password'}
                value={apiKeys.twilioVerifyServiceSid}
                onChange={(e) => {
                  setApiKeys({ ...apiKeys, twilioVerifyServiceSid: e.target.value });
                  setHasChanges(true);
                }}
                fullWidth
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => toggleApiKeyVisibility('twilioVerifyServiceSid')}>
                        {showApiKeys.twilioVerifyServiceSid ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
                helperText="Twilio Verify API for secure phone verification"
              />

              <Divider />

              <Typography variant="subtitle2" fontWeight="bold">Stripe Payment Configuration</Typography>

              <TextField
                label="Stripe Publishable Key"
                type={showApiKeys.stripePublishableKey ? 'text' : 'password'}
                value={apiKeys.stripePublishableKey}
                onChange={(e) => {
                  setApiKeys({ ...apiKeys, stripePublishableKey: e.target.value });
                  setHasChanges(true);
                }}
                fullWidth
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => toggleApiKeyVisibility('stripePublishableKey')}>
                        {showApiKeys.stripePublishableKey ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
                helperText="Public key for client-side"
              />

              <TextField
                label="Stripe Secret Key"
                type={showApiKeys.stripeSecretKey ? 'text' : 'password'}
                value={apiKeys.stripeSecretKey}
                onChange={(e) => {
                  setApiKeys({ ...apiKeys, stripeSecretKey: e.target.value });
                  setHasChanges(true);
                }}
                fullWidth
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => toggleApiKeyVisibility('stripeSecretKey')}>
                        {showApiKeys.stripeSecretKey ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
                helperText="Secret key for server-side (keep secure)"
              />

              <Divider />

              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2, fontStyle: 'italic' }}>
                Note: SMS and phone verification are now handled by Twilio Verify API (configured above). 
                Email services are configured separately via AWS SES or SMTP settings.
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <Button
                variant="contained"
                startIcon={<Save />}
                onClick={handleSaveApiKeys}
                color="error"
              >
                Save API Keys
              </Button>
            </Box>
          </CardContent>
        </Card>
      </TabPanel>

      <Snackbar
        open={alert.show}
        autoHideDuration={6000}
        onClose={() => setAlert({ ...alert, show: false })}
      >
        <Alert onClose={() => setAlert({ ...alert, show: false })} severity={alert.severity}>
          {alert.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SettingsPage;
